import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

/**
 * Global hook to intercept 403 "email_not_verified" responses.
 * Shows the EmailVerificationModal and retries the action on success.
 */
const useEmailVerificationGuard = () => {
  const [showModal, setShowModal] = useState(false);
  const retryCallbackRef = useRef(null);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response?.status === 403 &&
          error.response?.data?.detail === 'email_not_verified'
        ) {
          setShowModal(true);
          // Don't reject — let the caller know verification is needed
          return Promise.reject({ __emailVerificationRequired: true, originalError: error });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const onVerified = useCallback(() => {
    setShowModal(false);
    if (retryCallbackRef.current) {
      retryCallbackRef.current();
      retryCallbackRef.current = null;
    }
  }, []);

  const onClose = useCallback(() => {
    setShowModal(false);
    retryCallbackRef.current = null;
  }, []);

  return { showModal, onVerified, onClose };
};

export default useEmailVerificationGuard;
